import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Work, People, EmojiEvents, Favorite } from '@mui/icons-material';
import JobListing from '../components/careers/JobListing';

const Careers = () => {
    const jobs = [
        {
            id: 1,
            title: 'Développeur Backend Senior',
            location: 'Lubumbashi / Remote',
            type: 'CDI',
            duration: 'Temps plein',
            description: 'Nous recherchons un développeur backend expérimenté pour rejoindre notre équipe technique et contribuer au développement de notre API de paiement.',
            tags: ['Node.js', 'Python', 'API', 'PostgreSQL', 'Redis']
        },
        {
            id: 2,
            title: 'Ingénieur DevOps',
            location: 'Lubumbashi / Remote',
            type: 'CDI',
            duration: 'Temps plein',
            description: 'Rejoignez notre équipe pour maintenir et améliorer notre infrastructure cloud et assurer la haute disponibilité de nos services.',
            tags: ['AWS', 'Docker', 'Kubernetes', 'CI/CD', 'Terraform']
        },
        {
            id: 3,
            title: 'Développeur Frontend',
            location: 'Lubumbashi / Remote',
            type: 'CDI',
            duration: 'Temps plein',
            description: 'Créez des interfaces utilisateur exceptionnelles pour nos produits et outils développeur.',
            tags: ['React', 'TypeScript', 'Tailwind CSS', 'Next.js']
        }
    ];

    const benefits = [
        {
            title: 'Environnement de travail moderne',
            description: 'Bureaux modernes et télétravail flexible',
            icon: Work
        },
        {
            title: 'Équipe passionnée',
            description: 'Rejoignez une équipe dynamique et collaborative',
            icon: People
        },
        {
            title: 'Développement professionnel',
            description: 'Formations et opportunités de croissance',
            icon: EmojiEvents
        },
        {
            title: 'Culture d\'entreprise',
            description: 'Équilibre vie professionnelle/personnelle',
            icon: Favorite
        }
    ];

    return (
        <>
            <Helmet>
                <title>Carrières - Netikash Developer Portal</title>
                <meta name="description" content="Rejoignez l'équipe Netikash : offres d'emploi, culture d'entreprise et opportunités de carrière." />
                <meta property="og:title" content="Carrières chez Netikash" />
                <link rel="canonical" href="https://dev.netikash.com/careers" />
            </Helmet>
            <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
                {/* Hero Section */}
                <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
                                <Work className="mr-2" />
                                <span className="font-semibold text-lg">CARRIÈRES</span>
                            </div>
                            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                                Rejoignez notre équipe
                            </h1>
                            <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
                                Construisez l'avenir des paiements numériques avec Netikash
                            </p>
                        </div>
                    </div>
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    {/* Culture Section */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Pourquoi Travailler chez Netikash ?</h2>
                        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                            {benefits.map((benefit, index) => {
                                const Icon = benefit.icon;
                                return (
                                    <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 text-center">
                                        <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                            <Icon className="text-amber-600 text-3xl" />
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-900 mb-2">{benefit.title}</h3>
                                        <p className="text-gray-600 text-sm">{benefit.description}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    {/* Job Listings */}
                    <section className="mb-16">
                        <h2 className="text-3xl font-bold text-gray-900 mb-8">Postes Ouverts</h2>
                        {jobs.length === 0 ? (
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                                <Work className="text-gray-400 text-6xl mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">Aucun poste ouvert pour le moment</h3>
                                <p className="text-gray-600 mb-6">
                                    Consultez régulièrement cette page ou envoyez-nous une candidature spontanée.
                                </p>
                                <a
                                    href="mailto:careers@netikash.com?subject=Candidature spontanée"
                                    className="inline-block px-6 py-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-yellow-700 transition-all"
                                >
                                    Candidature spontanée
                                </a>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                {jobs.map(job => (
                                    <JobListing key={job.id} job={job} />
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Candidature Spontanée */}
                    <section className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl shadow-sm border border-amber-200 p-8 md:p-12">
                        <div className="text-center">
                            <h2 className="text-3xl font-bold text-gray-900 mb-4">Candidature Spontanée</h2>
                            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
                                Vous ne trouvez pas de poste qui correspond à votre profil ? Envoyez-nous une candidature spontanée. 
                                Nous gardons toutes les candidatures en base et nous vous contacterons si un poste correspond à votre profil.
                            </p>
                            <a
                                href="mailto:careers@netikash.com?subject=Candidature spontanée"
                                className="inline-block px-8 py-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-white rounded-lg font-medium hover:from-amber-600 hover:to-yellow-700 transition-all"
                            >
                                Envoyer une candidature spontanée
                            </a>
                        </div>
                    </section>

                    {/* Processus de Recrutement */}
                    <section className="mt-12 bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Processus de Recrutement</h2>
                        <div className="grid md:grid-cols-4 gap-6">
                            {[
                                { step: 1, title: 'Candidature', description: 'Postulez via email ou notre formulaire' },
                                { step: 2, title: 'Évaluation', description: 'Review de votre profil et test technique' },
                                { step: 3, title: 'Entretien', description: 'Entretien avec l\'équipe' },
                                { step: 4, title: 'Offre', description: 'Proposition et intégration' }
                            ].map(item => (
                                <div key={item.step} className="text-center">
                                    <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                                        {item.step}
                                    </div>
                                    <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                                    <p className="text-sm text-gray-600">{item.description}</p>
                                </div>
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

export default Careers;